import { useState, useEffect, useContext } from "react";
import {
  Grid,
  Typography,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Box,
} from "@mui/material";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import OrgSelect from "./components/OrgSelect";
import InvoiceList from "./components/InvoiceList";
import PaymentDialog from "../InvoiceDetail/components/PaymentDialog";
import ManualInvoice from "../../components/ManualInvoice";
import { EventContext } from "../../contexts/EventContext";
import request from "../../utilities/requests";
import { toast } from "../../utilities/SnackBarUtils";

const Invoices = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { orgId } = useParams();
  const { userData } = useContext(EventContext);
  const [query, setQuery] = useState({ status: "open" });
  const [org, setOrg] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState([]);
  const [totalSelected, setTotalSelected] = useState(0);
  const [statusFilter, setStatusFilter] = useState("open");
  const [loading, setLoading] = useState(false);
  const [batchRunning, setBatchRunning] = useState(false);
  const [showBtns, setShowBtns] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);

  useEffect(() => {
    if (Object.keys(query).length > 0) {
      getData();
      setSelected([]);
    }
    if (org !== orgId) {
      setOrg(orgId);
    }
  }, [query, orgId]);

  useEffect(() => {
    if (selected.length > 0 && ["open", "overdue"].includes(statusFilter)) {
      const total = invoices
        .filter((i) => selected.includes(i._id))
        .reduce((sum, item) => sum + item.amountDue, 0);
      // const total = selected.reduce((sum, item) => sum + item.amountDue, 0);
      setTotalSelected(total);
      setShowBtns(true);
    } else {
      setShowBtns(false);
      setTotalSelected(0);
    }
  }, [selected]);

  useEffect(() => {
    const pi = searchParams.get("payment_intent");
    if (pi && !paymentIntentId) {
      setPaymentIntentId(pi);
      getData();
      //TODO: Show success here
    }
  }, [searchParams]);

  const getData = async () => {
    setLoading(true);
    let url = "contracts/invoice?";
    Object.entries(query).forEach(([key, value]) => {
      url += `${encodeURIComponent(key)}=${encodeURIComponent(value)}&`;
    });
    if (orgId) {
      url += `orgId=${orgId}`;
    }
    const result = await request(url);
    if (result.success) {
      setInvoices(result.invoices);
    }
    setLoading(false);
  };

  const handleOrgChange = (orgId) => {
    setOrg(orgId);
    navigate(`/invoices/${orgId}`);
  };

  const handleInvoiceSelect = (inv) => {
    navigate(`/invoice/${inv._id}`);
  };

  const runBatch = async () => {
    setBatchRunning(true);
    const result = await request("contracts/invoice/batch", "post", {});
    if (result.success) {
      toast.success("Batch run complete");
    }
    setBatchRunning(false);
  };

  return (
    <Grid container style={{ padding: 20 }} spacing={3} justifyContent="center">
      <Grid item xs={12}>
        <Grid container>
          <Grid item xs={2}>
            <Button size="small" onClick={() => navigate("/admin")}>
              back to admin
            </Button>
          </Grid>
          <Grid item xs={8}>
            {" "}
            <Typography variant="h5" align="center">
              Invoices
            </Typography>
          </Grid>
          <Grid item xs={2}></Grid>
        </Grid>
      </Grid>
      <Grid item xs={6}>
        {userData?.role === "superadmin" ? (
          <OrgSelect org={org} setOrg={handleOrgChange} />
        ) : null}
      </Grid>

      <Grid
        item
        xs={12}
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={(e, val) => {
            if (val !== null) {
              setStatusFilter(val);
              setQuery((prev) => ({ ...prev, status: val }));
            }
          }}
          aria-label="invoice status filter"
          size="small"
        >
          <ToggleButton value="open" aria-label="open invoices">
            Open
          </ToggleButton>
          <ToggleButton value="overdue" aria-label="overdue invoices">
            Overdue
          </ToggleButton>
          <ToggleButton value="disputed" aria-label="disputed invoices">
            Disputed
          </ToggleButton>
          <ToggleButton value="paid" aria-label="paid invoices">
            Paid
          </ToggleButton>
          <ToggleButton value="void" aria-label="canceled invoices">
            Canceled
          </ToggleButton>
        </ToggleButtonGroup>
        {userData?.role === "superadmin" ? (
          <Button
            onClick={() => setManualOpen(true)}
            variant="outlined"
            size="small"
            style={{ height: 30 }}
          >
            Create Invoice
          </Button>
        ) : null}
        {userData?.role === "superadmin" ? (
          <Button onClick={runBatch}>
            {batchRunning ? <CircularProgress size={20} /> : "Run Batch"}
          </Button>
        ) : null}
      </Grid>
      {showBtns ? (
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            alignItems: "center",
          }}
        >
          <Typography variant="button">Pay Now with:</Typography>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <div style={{ flex: 1 }}></div>
            <div style={{ display: "flex", gap: 10 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setPaymentMethod("ach");
                  setDialogOpen(true);
                }}
              >
                ACH
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setPaymentMethod("card");
                  setDialogOpen(true);
                }}
              >
                Credit Card
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setPaymentMethod("zelle");
                  setDialogOpen(true);
                }}
              >
                Zelle
              </Button>
            </div>
            <div
              style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}
            >
              <Typography style={{ paddingRight: 10 }}>
                Total: ${totalSelected}
              </Typography>
            </div>
          </div>
        </Grid>
      ) : null}
      <Grid item xs={12}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : invoices.length === 0 ? (
          <Typography align="center" sx={{ mt: 4 }}>
            No invoices to display
          </Typography>
        ) : (
          <InvoiceList
            invoices={invoices}
            setInvoice={handleInvoiceSelect}
            status={statusFilter}
            selected={selected}
            setSelected={setSelected}
          />
        )}
      </Grid>
      <PaymentDialog
        open={dialogOpen}
        close={() => setDialogOpen(false)}
        invoiceId={selected}
        method={paymentMethod}
      />
      <ManualInvoice
        open={manualOpen}
        close={() => {
          setManualOpen(false);
          getData();
        }}
        orgId={orgId}
      />
    </Grid>
  );
};

export default Invoices;
